import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { InviteStatus, ProjectAccessType } from '~/client/graph'
import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { useTableList } from '~/client/src/desktop/tables/useTableList'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import MeCompanyQuickFilters from '~/client/src/shared/components/Filters/MeCompanyQuickFilters/MeCompanyQuickFilters'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { Loader } from '~/client/src/shared/components/Loader'
import { ViewOptionsPopover } from '~/client/src/shared/components/ViewOptions'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'
import canSendInviteToUser from '~/client/src/shared/utils/canSendInviteToUser'

import Header from './compound/Header'
import Modals from './compound/Modals'
import { usePhoneActions } from './compound/PhoneActions'
import SelectionToolbar from './compound/SelectionToolbar'
import UserDirectoryTable from './compound/Table'
import { useMeCompanyFilter } from './hooks/useMeCompanyFilter'
import { useModalState } from './hooks/useModalState'
import { useUserActions } from './hooks/useUserActions'
import { useUserRecords } from './hooks/useUserRecords'
import { UserDirectoryColumnId } from './types'
import { buildUserActionsColumn, buildUserFields } from './userFields'

interface ProvidedStores {
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  tagsStore: TagsStore
  companiesStore: CompaniesStore
  state: DesktopInitialState
}

function UserDirectoryView(): React.ReactElement {
  const {
    projectMembersStore,
    userProjectsStore,
    tagsStore,
    companiesStore,
    state,
  } = React.useContext(MobXProviderContext) as unknown as ProvidedStores

  const modalState = useModalState()
  const actions = useUserActions()
  const records = useUserRecords()
  // Bumped after a phone change so the open user-details dialog clears its number.
  const [phoneClearToken, setPhoneClearToken] = React.useState(0)
  const phoneActions = usePhoneActions(records.rows, () =>
    setPhoneClearToken(t => t + 1),
  )
  const { apply: applyMeCompanyFilter, props: meCompanyProps } =
    useMeCompanyFilter()

  const handleEditUser = React.useCallback(
    (userId: string) => {
      const user = projectMembersStore.getById(userId)
      if (!user) return
      modalState.openEditUsers([user.asJson as IServiceUserDto])
    },
    [projectMembersStore, modalState],
  )

  const rowActionHandlers = React.useMemo(
    () => ({
      onEdit: handleEditUser,
      onInvite: (userId: string) => {
        const user = projectMembersStore.getById(userId)
        if (user) modalState.openInvite([user])
      },
      onApprove: (userId: string) => {
        const user = projectMembersStore.getById(userId)
        if (user) modalState.openApprove([user])
      },
      onSetNotInvited: (userId: string) => {
        const user = projectMembersStore.getById(userId)
        if (user) modalState.openSetNotInvited([user])
      },
      onResetPassword: (userId: string) => {
        const user = projectMembersStore.getById(userId)
        if (user) actions.resetPassword(user)
      },
      onManagePhone: phoneActions.handlers.onManagePhone,
    }),
    [handleEditUser, projectMembersStore, modalState, actions, phoneActions],
  )
  const tableFields = React.useMemo(
    () =>
      buildUserFields({
        sortedCompanyIds: records.sortedCompanyIds,
        tagsStore,
      }),
    [records.sortedCompanyIds, tagsStore],
  )
  const columns = React.useMemo(
    () => [...tableFields.columns, buildUserActionsColumn(rowActionHandlers)],
    [rowActionHandlers, tableFields.columns],
  )
  const tableList = useTableList({
    records: records.rows,
    schema: tableFields.schema,
    displayColumns: columns,
    columnConfig: {
      options: tableFields.columnConfig,
      storageKey: LocalStorageKey.UserDirectoryColumnConfig,
      projectId: state.activeProject?.id,
      userId: state.user?.id,
    },
    selectable: true,
    selectionColumnId: UserDirectoryColumnId.Checkbox,
    pinnedRightColumnIds: [UserDirectoryColumnId.Actions],
    extraFilters: applyMeCompanyFilter,
    groupingAllowNone: true,
  })

  const selectedUsers = React.useMemo(
    () => tableList.selection.selectedRecords.map(row => row.user),
    [tableList.selection.selectedRecords],
  )

  const hasInvitableSelected = React.useMemo(
    () =>
      selectedUsers.some(user => canSendInviteToUser(user, userProjectsStore)),
    [selectedUsers, userProjectsStore],
  )

  const hasApprovableSelected = React.useMemo(
    () =>
      selectedUsers.some(user =>
        UserProject.isPendingApproval(user, userProjectsStore),
      ),
    [selectedUsers, userProjectsStore],
  )

  const handleAddNewUser = React.useCallback(() => {
    const activeProjectId = state.activeProject?.id
    if (!activeProjectId) return
    const newMember = Object.assign(new User(null, null), {
      userProjectSettings: new UserProject(
        null,
        activeProjectId,
        null,
        InviteStatus.NotInvited,
        ProjectAccessType.Member,
        false,
        null,
      ).toDto(),
    }).asJson as IServiceUserDto
    modalState.openAddUser(newMember)
  }, [state.activeProject, modalState])

  const handleAddProjectMember = React.useCallback(
    (companyId: string | null) => {
      const activeProjectId = state.activeProject?.id
      if (!activeProjectId) return
      const newMember = Object.assign(new User(null, null), {
        userProjectSettings: new UserProject(
          null,
          activeProjectId,
          null,
          InviteStatus.NotInvited,
          ProjectAccessType.Member,
          false,
          companyId || null,
        ).toDto(),
      }).asJson as IServiceUserDto
      modalState.openAddUser(newMember)
    },
    [state.activeProject, modalState],
  )

  const handleEditSelected = React.useCallback(() => {
    if (selectedUsers.length === 0) return
    modalState.openBulkEdit()
  }, [selectedUsers, modalState])

  const handleInviteSelected = React.useCallback(() => {
    const invitable = selectedUsers.filter(user =>
      canSendInviteToUser(user, userProjectsStore),
    )
    if (invitable.length === 0) return
    modalState.openInvite(invitable)
  }, [selectedUsers, userProjectsStore, modalState])

  const handleApproveSelected = React.useCallback(() => {
    const approvable = selectedUsers.filter(user =>
      UserProject.isPendingApproval(user, userProjectsStore),
    )
    if (approvable.length === 0) return
    modalState.openApprove(approvable)
  }, [selectedUsers, userProjectsStore, modalState])

  const handleExport = React.useCallback(() => {
    actions.exportCsv()
  }, [actions])

  // Set when a CSV upload fails (partly) because some numbers are already held
  // by other users, so an admin can reassign them and re-import.
  const [csvReassignPrompt, setCsvReassignPrompt] = React.useState<{
    file: File
    count: number
  } | null>(null)
  const isAdmin = !!(
    state.user &&
    userProjectsStore &&
    UserProject.isAdmin(state.user, userProjectsStore)
  )

  const handleUpload = React.useCallback(
    (file: File) => {
      void actions.importCsv(file, {
        onPhoneClaimConflicts: isAdmin
          ? count => setCsvReassignPrompt({ file, count })
          : undefined,
      })
    },
    [actions, isAdmin],
  )

  const confirmCsvReassign = React.useCallback(() => {
    if (!csvReassignPrompt) return
    void actions.importCsv(csvReassignPrompt.file, {
      reassignClaimedPhone: true,
    })
    setCsvReassignPrompt(null)
  }, [actions, csvReassignPrompt])

  const handleConnectProcore = React.useCallback(() => {
    actions.getUsersFromProcore(
      companies => {
        if (!companies?.length) {
          modalState.openAlert(Localization.translator.procoreNoCompaniesAccess)
          return
        }
        modalState.openProcore(companies)
      },
      err =>
        modalState.openAlert(
          (err instanceof Error ? err.message : err) ||
            Localization.translator.procoreConnectionFailed,
        ),
    )
  }, [actions, modalState])

  const isExporting = !!state.loading.get(e.DOWNLOAD_PROJECT_MEMBERS)
  const isUploading = !!state.loading.get(e.UPLOAD_PROJECT_MEMBERS)
  const isConnectingProcore = !!state.loading.get(e.GET_USERS_FROM_PROCORE)

  if (records.loading) {
    return (
      <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center">
        <Loader hint={Localization.translator.loadingTeamMembers} />
      </section>
    )
  }

  return (
    <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:py-4">
      <Header />

      <div className="tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-4 tw:pb-2">
        <SearchInput {...tableList.searchProps} />
        <TableFilters {...tableList.filterProps}>
          <MeCompanyQuickFilters {...meCompanyProps} />
        </TableFilters>
        <div className="tw:flex-1" />
        <ViewOptionsPopover {...tableList.viewOptionsProps} />
      </div>

      <SelectionToolbar
        list={tableList}
        hasInvitableSelected={hasInvitableSelected}
        hasApprovableSelected={hasApprovableSelected}
        onEdit={handleEditSelected}
        onInvite={handleInviteSelected}
        onApprove={handleApproveSelected}
        onAddUser={handleAddNewUser}
        onExport={handleExport}
        onUpload={handleUpload}
        onConnectProcore={handleConnectProcore}
        isExporting={isExporting}
        isUploading={isUploading}
        isConnectingProcore={isConnectingProcore}
      />

      <UserDirectoryTable
        list={tableList}
        companiesStore={companiesStore}
        onAddProjectMember={handleAddProjectMember}
        onEditUser={handleEditUser}
      />

      <Modals
        modalState={modalState}
        actions={actions}
        selectedUsers={selectedUsers}
        hasInvitableSelected={hasInvitableSelected}
        clearSelection={tableList.selection.clear}
        onManagePhone={phoneActions.handlers.onManagePhone}
        phoneClearToken={phoneClearToken}
      />

      {phoneActions.dialogs}

      <ConfirmDialog
        isOpen={!!csvReassignPrompt}
        onCancelClicked={() => setCsvReassignPrompt(null)}
        onDoneClicked={confirmCsvReassign}
        doneTitle="Reassign and import"
      >
        <div className="tw:text-body-md tw:whitespace-pre-line">
          {csvReassignPrompt?.count === 1
            ? '1 phone number in this CSV is already assigned to another user. Reassign it to the CSV’s user and import? The current holder will lose the number — and, if it is their only way to sign in, access to StruxHub. If the CSV’s user already has a different number, it will be replaced.'
            : `${
                csvReassignPrompt?.count ?? 0
              } phone numbers in this CSV are already assigned to other users. Reassign them to the CSV’s users and import? The current holders will lose those numbers — and, where a number is someone’s only way to sign in, their access to StruxHub. CSV users who already have a different number will have it replaced.`}
        </div>
      </ConfirmDialog>
    </section>
  )
}

export default observer(UserDirectoryView)
